/**
 * IntegrationsScreen - Updated for Connections Service
 * 
 * Displays connected integrations using connections-service API.
 * Tracks connection status in Firestore via connections-service.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  SpaceBackground,
  GlassCard,
  Header,
  Sidebar,
  BottomNav,
} from '../components/shared';
import {
  ChevronLeft,
  Calendar,
  Mail,
  MessageCircle,
  Plus,
  Check,
  Loader,
  Twitter,
  Linkedin,
  Instagram,
  Facebook,
  MessageSquare,
} from 'lucide-react';
import { 
  listConnections, 
  removeConnection,
  Connection 
} from '../services/connectionsApi';
import { connectIntegration } from '../services/integrationsApi';
// connectGoogleService removed — all OAuth now goes through server-side orbit-gateway flow

// Integration type from URL params
type IntegrationType = 'calendar' | 'email' | 'messages' | 'social';

// Available integrations config
const AVAILABLE_INTEGRATIONS = {
  calendar: [
    { id: 'google_calendar', name: 'Google Calendar', icon: Calendar },
    { id: 'outlook_calendar', name: 'Outlook Calendar', icon: Calendar },
    { id: 'apple_calendar', name: 'Apple Calendar', icon: Calendar },
  ],
  email: [
    { id: 'gmail', name: 'Gmail', icon: Mail },
    { id: 'outlook_email', name: 'Outlook', icon: Mail },
    { id: 'icloud_email', name: 'iCloud Mail', icon: Mail },
  ],
  messages: [
    { id: 'slack', name: 'Slack', icon: MessageSquare },
    { id: 'teams', name: 'Microsoft Teams', icon: MessageCircle },
    { id: 'discord', name: 'Discord', icon: MessageCircle },
  ],
  social: [
    { id: 'twitter', name: 'Twitter/X', icon: Twitter },
    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin },
    { id: 'instagram', name: 'Instagram', icon: Instagram },
    { id: 'facebook', name: 'Facebook', icon: Facebook },
  ],
};

// Connection status component
const ConnectionStatus = ({ 
  connected, 
  loading, 
  onConnect, 
  onDisconnect 
}: {
  connected: boolean;
  loading: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}) => (
  <div className="flex items-center space-x-2">
    {loading ? (
      <Loader className="w-5 h-5 text-cyan-400 animate-spin" />
    ) : connected ? (
      <>
        <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-400/10 border border-green-400/30 rounded-full">
          <Check className="w-4 h-4 text-green-400" />
          <span className="text-sm text-green-400">Connected</span>
        </div>
        <button
          onClick={onDisconnect}
          className="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 border border-red-400/30 rounded-full hover:bg-red-400/10 transition-all"
        >
          Disconnect
        </button>
      </>
    ) : (
      <button
        onClick={onConnect}
        className="flex items-center space-x-2 px-4 py-2 bg-cyan-400 text-black rounded-full font-semibold hover:bg-cyan-300 transition-all shadow-[0_0_20px_rgba(34,211,238,0.5)]"
      >
        <Plus className="w-4 h-4" />
        <span>Connect</span>
      </button>
    )}
  </div>
);

// Integration card component
const IntegrationCard = ({
  name,
  icon: Icon,
  connected,
  loading,
  onConnect,
  onDisconnect
}: {
  name: string;
  icon: any;
  connected: boolean;
  loading: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}) => (
  <GlassCard className="p-4 flex items-center justify-between">
    <div className="flex items-center space-x-4">
      <div className="w-12 h-12 rounded-xl bg-cyan-400/10 flex items-center justify-center">
        <Icon className="w-6 h-6 text-cyan-400" />
      </div>
      <div>
        <h3 className="font-semibold text-white">{name}</h3>
        <p className="text-sm text-cyan-300/70">
          {connected ? 'Ready to use' : 'Not connected'}
        </p>
      </div>
    </div>
    <ConnectionStatus
      connected={connected}
      loading={loading}
      onConnect={onConnect}
      onDisconnect={onDisconnect}
    />
  </GlassCard>
);

// Main Integrations Screen
const IntegrationsScreen = () => {
  const { type = 'calendar' } = useParams<{ type: IntegrationType }>();
  const navigate = useNavigate();
  
  const [connections, setConnections] = useState<Record<string, Connection>>({});
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Load connections on mount
  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      const conns = await listConnections();
      const connMap = conns.reduce((acc, conn) => {
        acc[conn.service_id] = conn;
        return acc;
      }, {} as Record<string, Connection>);
      setConnections(connMap);
    } catch (error) {
      console.error('Failed to load connections:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleConnect = async (serviceId: string) => {
    setLoading(prev => ({ ...prev, [serviceId]: true }));
    
    try {
      // ALL services go through the server-side OAuth flow via orbit-gateway.
      // This is provider-agnostic: Google, Slack, iCloud, or any provider
      // discovered on the fly all use the same backend path.
      // orbit-gateway builds the auth URL, handles the callback, exchanges
      // the code for tokens, and stores them in Identity Vault.
      const result = await connectIntegration(type, serviceId);
      
      if (result.success && result.auth_url) {
        // Has OAuth URL — redirect to auth flow
        window.location.href = result.auth_url;
      } else if (result.success) {
        // Integration discovered or being built — show status to user
        const status = (result as any).status || 'unknown';
        const message = (result as any).message || `${serviceId} connection in progress`;
        if (status === 'needs_user_auth') {
          alert(message);
        } else if (status === 'auto_setup' || status === 'needs_developer_setup') {
          // System is handling it automatically or still provisioning the developer app.
          alert(`${message}\n\nSuperJarvis is handling the setup automatically. Check back in a moment.`);
          await loadConnections();
        } else {
          alert(message);
        }
      } else {
        // Show the building/error message
        const errorMsg = (result as any).error || `Unable to connect ${serviceId}`;
        console.log(`[Integrations] ${serviceId}: ${errorMsg}`);
        alert(errorMsg);
      }
    } catch (error) {
      console.error(`Failed to connect ${serviceId}:`, error);
    } finally {
      setLoading(prev => ({ ...prev, [serviceId]: false }));
    }
  };

  const handleDisconnect = async (serviceId: string) => {
    setLoading(prev => ({ ...prev, [serviceId]: true }));
    
    try {
      await removeConnection(serviceId);
      await loadConnections();
    } catch (error) {
      console.error(`Failed to disconnect ${serviceId}:`, error);
    } finally {
      setLoading(prev => ({ ...prev, [serviceId]: false }));
    }
  };

  const currentIntegrations = AVAILABLE_INTEGRATIONS[type] || [];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      <SpaceBackground />
      
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onSettingsClick={() => navigate('/settings')} />
      
      <main className="ml-16 pb-24 pt-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/30 transition-all"
              >
                <ChevronLeft className="w-5 h-5 text-cyan-400" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white capitalize">
                  {type} Integrations
                </h1>
                <p className="text-cyan-300/70">
                  Connect your {type} services to SuperJarvis
                </p>
              </div>
            </div>
          </div>

          {/* Integration Type Tabs */}
          <div className="flex space-x-2 mb-8 overflow-x-auto">
            {(['calendar', 'email', 'messages', 'social'] as const).map((t) => (
              <button
                key={t}
                onClick={() => navigate(`/integrations/${t}`)}
                className={`px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap ${
                  type === t
                    ? 'bg-cyan-400 text-black'
                    : 'bg-cyan-400/10 text-cyan-400 hover:bg-cyan-400/20 border border-cyan-400/30'
                }`}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {/* Integration Cards */}
          {initialLoading ? (
            <div className="flex justify-center py-12">
              <Loader className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {currentIntegrations.map((integration) => (
                <IntegrationCard
                  key={integration.id}
                  name={integration.name}
                  icon={integration.icon}
                  connected={!!connections[integration.id]?.connected}
                  loading={loading[integration.id] || false}
                  onConnect={() => handleConnect(integration.id)}
                  onDisconnect={() => handleDisconnect(integration.id)}
                />
              ))}
            </div>
          )}

          {/* Info Card */}
          <GlassCard className="mt-8 p-6">
            <h3 className="font-semibold text-white mb-2">
              About Connections
            </h3>
            <p className="text-cyan-300/70 text-sm leading-relaxed">
              Your connections are stored securely and only used when you explicitly request it. 
              SuperJarvis never shares your credentials. OAuth tokens are encrypted in Identity Vault.
            </p>
          </GlassCard>
        </div>
      </main>
      
      <BottomNav />
    </div>
  );
};

export default IntegrationsScreen;
